import type { SimEvent } from "../protocol/events";
import { addComposition, compositionsEqual, maxMultiple, scaleComposition, subtractComposition } from "./composition";
import { resolveHoldRecipeForPacket, type EngineNode, type EngineState } from "./state";

/**
 * Hold completion for one processing node (spec 0025). Runs inside step 2's
 * node loop, immediately after `processThermalRegulation` — no tick step is
 * added. Per packet, not pooled: `resolveHoldRecipeForPacket` resolves
 * against each packet's own composition, so two incubators of the same kind
 * can run different holds with no cross-talk.
 *
 * A hold whose `outputs` differ from `inputs` rewrites the packet in place
 * on completion (`composition - k·inputs + k·outputs`, `k = maxMultiple`)
 * and emits the ordinary `reaction.fired` (`isExothermic: false`), so FX and
 * cue handlers need no change. A hold whose `outputs` equal `inputs` — every
 * hold shipped before this spec — completes silently, exactly as before:
 * no event, no composition change. Either way `hasCompletedHold` is set, so
 * `isHeldByIncubator` releases the packet on its next step 3 and never
 * re-holds it against a different recipe in the same node visit.
 */
export function processHoldTransforms(state: EngineState, node: EngineNode, events: SimEvent[]): void {
  if (node.isDestroyed || node.kind !== "incubator" || node.buffer.length === 0) return;
  // Same every-other-tick cadence as the other step-2 passes (`03-hazards.md` §2.5/§3.3).
  if (!node.sludgeActiveThisTick || !node.verminActiveThisTick) return;

  for (const packet of node.buffer) {
    if (packet.hasCompletedHold) continue;
    const recipe = resolveHoldRecipeForPacket(state, node, packet);
    if (recipe === undefined) continue;
    if (recipe.minTemperature !== undefined && packet.temperature < recipe.minTemperature) continue;
    if (packet.holdTicks < recipe.holdDuration!) continue;

    packet.holdTicks = 0;
    packet.hasCompletedHold = true;

    if (compositionsEqual(recipe.outputs, recipe.inputs)) continue; // non-transforming: nothing to rewrite, no event

    const k = maxMultiple(packet.composition, recipe.inputs);
    if (k === 0) continue; // can't happen — resolveHoldRecipeForPacket already required contains() — but never emit on a zero multiple
    const produced = scaleComposition(recipe.outputs, k);
    packet.composition = addComposition(subtractComposition(packet.composition, scaleComposition(recipe.inputs, k)), produced);

    events.push({
      type: "reaction.fired",
      nodeId: node.id,
      recipeId: recipe.id,
      multiple: k,
      yieldComposition: { ...produced },
      isExothermic: false,
    });
  }
}
